<template>
  <div class="popup-create">
    
    <div class="left-section">

      <h1>{{ mode === 'edit' ? '✏️ 팝업 수정' : '📝 팝업 생성' }}</h1>
      <form @submit.prevent="handleSubmit">
        <div>
          <label for="title">제목</label>
          <input type="text" id="title" v-model="popup.title" required />
        </div>

        <div>
          <label for="content">내용</label>
          <ToastEditor v-model="popup.content" />
        </div>

        <div>
          <label for="startTs">시작일</label>
          <input type="date" id="startTs" v-model="popup.startTs" required />
        </div>

        <div>
          <label for="endTs">종료일</label>
          <input type="date" id="endTs" v-model="popup.endTs" required />
        </div>

        <div>
          <label for="image">배경 이미지</label>
          <label for="image" class="custom-file-label">이미지 선택</label>
          <input type="file" id="image" @change="handleImageChange" />
        </div>

        <div>
          <label for="linkUrl">이미지 클릭 시 이동할 URL</label>
          <input
            type="url"
            id="linkUrl"
            v-model="popup.linkUrl"
            placeholder="https://example.com"
          />
        </div>

        <button @click="goBack" type="button" class="back-button">이전으로</button>
        
        <button type="submit">{{ mode === 'edit' ? '수정' : '등록' }}</button>
      </form>
    </div>

    <div class="right-section">
      <h2>미리보기</h2>

      <div class="popup-preview">
        <!-- 이미지가 있을 때 -->
        <a
          v-if="(imagePreview || popup.imageUrl) && popup.linkUrl"
          :href="popup.linkUrl"
          target="_blank"
          rel="noopener"
          class="image-container"
        >
          <img
            :src="imagePreview || popup.imageUrl"
            :style="{ width: imageNaturalSize.width + 'px', height: imageNaturalSize.height + 'px' }"
            alt="배경 이미지"
            class="preview-image"
          />
          <div v-if="isMeaningfulContent(popup.content)" class="popup-content">
            <div v-html="popup.content"></div>
          </div>
        </a>

        <!-- 링크가 없을 때 기존 구조 유지 -->
        <div
          v-else-if="imagePreview || popup.imageUrl"
          class="image-container"
        >
          <img
            :src="imagePreview || popup.imageUrl"
            :style="{ width: imageNaturalSize.width + 'px', height: imageNaturalSize.height + 'px' }"
            alt="배경 이미지"
            class="preview-image"
          />
          <div v-if="isMeaningfulContent(popup.content)" class="popup-content">
            <div v-html="popup.content"></div>
          </div>
        </div>

        <!-- 이미지가 전혀 없을 때 -->
        <div v-else>
          <div v-html="popup.content"></div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ToastEditor from '@/components/ToastEditor.vue';
import axios from '@/utils/axios';

const route = useRoute();
const router = useRouter();
const mode = ref(route.path.includes('/edit') ? 'edit' : 'create');
const popupId = ref(route.params.id || null);
const imageNaturalSize = ref({ width: 0, height: 0 });

function goBack() {
  router.back()
}

function isMeaningfulContent(html) {
  if (!html) return false;
  // 태그/공백/개행 전부 날리고 텍스트 남는지 체크
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
  return text.length > 0;
}

const popup = ref({
  title: '',
  content: '',
  startTs: '',
  endTs: '',
  imageUrl: '', // 이미지 URL (수정 시 기존 이미지)
  linkUrl: '', 
});

const imagePreview = ref(null);

// 팝업 데이터 로드 (수정 모드일 경우)
onMounted(async () => {
  if (mode.value === 'edit' && popupId.value) {
    await loadPopupData();

    // 이미지 원본 크기 얻기
    if (popup.value.imageUrl) {
      const img = new window.Image();
      img.onload = () => {
        imageNaturalSize.value = { width: img.naturalWidth, height: img.naturalHeight };
      };
      img.src = popup.value.imageUrl;
    }
  }
});

// 기존 팝업 데이터 불러오기
const loadPopupData = async () => {
  try {
    const res = await axios.get(`/api/bx201/popup/${popupId.value}`);
    const { title, content, startTs, endTs, imageUrl, linkUrl } = res.data;
    popup.value = {
      title,
      content,
      startTs: toDateInputString(startTs),
      endTs: toDateInputString(endTs),
      imageUrl,
      linkUrl,     
    };
  } catch (err) {
    alert('팝업 정보를 불러오지 못했습니다.');
  }
};

// 이미지 미리보기
function handleImageChange(event) {
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      imagePreview.value = e.target.result;

      // 이미지 원본 사이즈 얻기
      const img = new window.Image();
      img.onload = () => {
        imageNaturalSize.value = { width: img.naturalWidth, height: img.naturalHeight };
        // 필요하다면 여기서 스타일 동적으로 조정 가능
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }
}

// 등록 또는 수정 처리
const handleSubmit = async () => {
  const formData = new FormData();
  formData.append(
    'popup',
    new Blob(
      [
        JSON.stringify({
          title: popup.value.title,
          content: popup.value.content,
          startTs: popup.value.startTs,
          endTs: popup.value.endTs,
          linkUrl: popup.value.linkUrl,
        }),
      ],
      { type: 'application/json' }
    )
  );

  if (imagePreview.value) {
    const imageFile = dataURLToBlob(imagePreview.value);
    formData.append('image', imageFile);
  }

  try {
    if (mode.value === 'edit') {
      await axios.put(`/api/bx201/popup/${popupId.value}`, formData);
      alert('팝업이 수정되었습니다!');
    } else {
      await axios.post('/api/bx201/popup', formData);
      alert('팝업이 등록되었습니다!');
    }
    router.push('/admin/popup');
  } catch (err) {
    alert('팝업 저장 실패');
  }
};

// DataURL을 Blob으로 변환
const dataURLToBlob = (dataURL) => {
  const byteString = atob(dataURL.split(',')[1]);
  const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uInt8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uInt8Array[i] = byteString.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: mimeString });
};

function toDateInputString(dateStr) {
  // null 또는 빈값 방지
  if (!dateStr) return '';
  // 이미 yyyy-MM-dd면 그대로
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // dateStr이 있으면 date 객체로 변환
  const d = new Date(dateStr);
  // 유효성 검사
  if (isNaN(d)) return '';
  // pad 함수
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
</script>


<style scoped>
.popup-create {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding-top: 80px;
  max-width: 1200px;
  margin: 0 auto;
}

.left-section {
  width: 45%;
}

.right-section {
  width: 45%;
  background: #f4f4f4;
  padding: 20px;
  border-radius: 10px;
}

.popup-preview {
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 10px;
  background: white;
  text-align: center;
}

.image-container {
  position: relative;
}

.popup-content {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  bottom: 20px;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 20px;
  overflow: auto;
}

form > div {
  margin-bottom: 20px;
}

label {
  display: block;
  font-weight: bold;
  margin-bottom: 6px;
}

input[type="text"],
input[type="date"] {
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 6px;
}

button[type="submit"] {
  background-color: #28a745;
  color: white;
  padding: 12px 20px;
  border: none;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
}

button[type="submit"]:hover {
  background-color: #218838;
}

.back-button {
  background-color: #28a745;
  color: white;
  padding: 12px 20px;
  border: none;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  margin-right: 5px;
}
.back-button:hover {
  background-color: #218838;
}

.preview-image {
  max-width: 100%;
  max-height: 350px;  /* or 원하는 제한값 */
  display: block;
  margin: 0 auto;
}
</style>
